import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fetchArtworks, fetchMediums, fetchArtworkArtists, type Artwork } from '../api'
import { ArtworkCard } from '../components/ArtworkCard'

export function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [artworks, setArtworks] = useState<Artwork[]>([])
  const [mediums, setMediums] = useState<string[]>([])
  const [artists, setArtists] = useState<{ name: string; slug: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const medium = searchParams.get('medium') ?? ''
  const artistSlug = searchParams.get('artistSlug') ?? ''
  const maxPrice = searchParams.get('maxPrice') ?? ''

  useEffect(() => {
    fetchMediums().then(setMediums).catch(() => undefined)
    fetchArtworkArtists().then(setArtists).catch(() => undefined)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(false)
    fetchArtworks({
      medium: medium || undefined,
      artistSlug: artistSlug || undefined,
      maxPrice: maxPrice || undefined,
    })
      .then(setArtworks)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [medium, artistSlug, maxPrice])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const next: Record<string, string> = {}
    const m = (fd.get('medium') as string).trim()
    const a = (fd.get('artistSlug') as string).trim()
    const p = (fd.get('maxPrice') as string).trim()
    if (m) next.medium = m
    if (a) next.artistSlug = a
    if (p) next.maxPrice = p
    setSearchParams(next)
  }

  const hasFilters = medium || artistSlug || maxPrice

  return (
    <main className="gallery container">
      <title>Originalene — Kjøp originalkunst fra norske kunstnere</title>
      <meta name="description" content="Bla gjennom unike originalkunstverk og kjøp direkte fra kunstneren. Malerier, tegninger og mer." />
      <h1 className="gallery-title">Verk</h1>

      <form className="filter-bar" onSubmit={handleSubmit}>
        <select
          key={`m-${medium}`}
          name="medium"
          className="filter-input"
          defaultValue={medium}
        >
          <option value="">Alle medium</option>
          {mediums.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          key={`a-${artistSlug}`}
          name="artistSlug"
          className="filter-input"
          defaultValue={artistSlug}
        >
          <option value="">Alle kunstnere</option>
          {artists.map(a => (
            <option key={a.slug} value={a.slug}>{a.name}</option>
          ))}
        </select>
        <input
          key={`p-${maxPrice}`}
          name="maxPrice"
          type="number"
          min="0"
          className="filter-input"
          placeholder="Makspris (NOK)"
          defaultValue={maxPrice}
        />
        <button type="submit" className="btn-apply">Bruk</button>
        {hasFilters && (
          <button type="button" className="btn-clear" onClick={() => setSearchParams({})}>
            Fjern
          </button>
        )}
      </form>

      {loading && <p className="state-loading">Laster inn…</p>}
      {error && <p className="state-error">Noe gikk galt. Vennligst prøv igjen.</p>}
      {!loading && !error && (
        <div className="artwork-grid">
          {artworks.map(a => <ArtworkCard key={a.id} artwork={a} />)}
          {artworks.length === 0 && (
            <p className="no-results">Ingen verk matcher dine filtre.</p>
          )}
        </div>
      )}
    </main>
  )
}
